import React, { useEffect, useState, useRef } from 'react';
import PopupBase from "../base/PopupBase.js";
import CustomButton from "../../components/buttons/CustomButton.js";
import IconPrint from '@mui/icons-material/Print';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import styles from "./styles.module.css";
import { CreatePDF, CreatePDFBlob } from '../../utils/Utils.js';
import { DataProviderInstance } from "../../api/DataProvider.js";
import DocumentDefaultTemplate from "../../components/documents-templates/DocumentDefaultTemplate.js";
import { useWhatsAppSender } from '../../contexts/WhatsAppSenderContext';
import { isWhatsAppPolicyError, getWhatsAppErrorMessage } from '../../utils/whatsappErrors';

function ViewDocumentPopup(props) {

    const { patientData, data, OnHide } = props;
    const { CompanyConfig, SendWhatsappDocument, GetWhatsappConfig } = DataProviderInstance();
    const { capabilities } = useWhatsAppSender();

    const professionalData = CompanyConfig.Resources[0];// FIXME: This is a placeholder. It should be the professional who created the document.

    const pdfContentRef = useRef(null);

    const [isPrinting, setIsPrinting] = useState(false);
    const [isSendingWhatsApp, setIsSendingWhatsApp] = useState(false);
    const [whatsAppFeedback, setWhatsAppFeedback] = useState({ type: '', message: '' });
    const [isWhatsappConfigured, setIsWhatsappConfigured] = useState(false);

    useEffect(() => {
        GetWhatsappConfig((result) => {
            if (result && result.config && result.config.instance_id && result.config.has_instance_token) {
                setIsWhatsappConfigured(true);
            } else {
                setIsWhatsappConfigured(false);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Botão de envio por WhatsApp só aparece quando há credenciais salvas E
    // a política do número (plataforma × próprio) permite documentos
    const canSendWhatsApp = isWhatsappConfigured && capabilities.document;

    function OnCloseClickHandler() {
        Hide();
    }

    function Hide() {
        OnHide();
    }

    function GetFileName() {
        let documentTypeName = data.title;
        let currentDate = new Date();
        let formattedDate = currentDate.getFullYear() + "_" + (currentDate.getMonth() + 1) + "_" + currentDate.getDate() + "_" + currentDate.getHours() + "_" + currentDate.getMinutes();
        let patientName = patientData.name;
        return `${patientName}_${formattedDate}_${documentTypeName}.pdf`.replace(/\s/g, '_');
    }

    function OnPrintHandler() {
        let fileName = GetFileName();
        let content = pdfContentRef.current;

        CreatePDF(content, fileName);
        setIsPrinting(false);
    }

    async function OnSendWhatsAppHandler() {
        // Compose full phone with country code (DDI) for Z-API
        const { normalizePhone } = await import('../../utils/phoneUtils');
        const phone = normalizePhone(patientData.phone || patientData.cellphone || '', patientData.countryCode);
        
        if (!phone) {
            setWhatsAppFeedback({ type: 'error', message: 'Paciente não possui telefone cadastrado.' });
            return;
        }

        setIsSendingWhatsApp(true);
        setWhatsAppFeedback({ type: '', message: '' });

        try {
            const content = pdfContentRef.current;
            const fileName = GetFileName();
            const pdfBlob = await CreatePDFBlob(content);

            const base64 = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(pdfBlob);
            });

            // Mensagem genérica para acompanhar o documento
            const caption = `Olá, segue o documento: ${data.title}`;

            SendWhatsappDocument(phone, base64, fileName, caption, patientData?.countryCode || '', (result) => {
                if (result && result.success) {
                    setWhatsAppFeedback({ type: 'success', message: 'Documento enviado com sucesso pelo WhatsApp.' });
                } else if (result && result.success === false && isWhatsAppPolicyError(result)) {
                    setWhatsAppFeedback({ type: 'error', message: getWhatsAppErrorMessage(result) });
                } else {
                    setWhatsAppFeedback({ type: 'error', message: 'Não foi possível enviar o documento pelo WhatsApp. Tente novamente.' });
                }
                setIsSendingWhatsApp(false);
            });
        } catch (error) {
            setWhatsAppFeedback({ type: 'error', message: 'Não foi possível enviar o documento pelo WhatsApp. Tente novamente.' });
            setIsSendingWhatsApp(false);
        }
    }

    function GetFooter() {
        return (
            <div className={styles.footer}>
                {whatsAppFeedback.message && (
                    <span className={whatsAppFeedback.type === 'success' ? styles.feedbackSuccess : styles.feedbackError}>
                        {whatsAppFeedback.message}
                    </span>
                )}
                <CustomButton variant="outlined" style="secondary" label={"Cancelar"} onClick={OnCloseClickHandler} />
                {canSendWhatsApp && (
                    <CustomButton
                        variant="outlined"
                        style="secondary"
                        label={isSendingWhatsApp ? "Enviando..." : "Enviar por WhatsApp"}
                        icon={<WhatsAppIcon />}
                        onClick={OnSendWhatsAppHandler}
                        disabled={isSendingWhatsApp}
                    />
                )}
                <CustomButton variant="contained" style="primary" label={"Imprimir"} icon={<IconPrint />} onClick={OnPrintHandler} />
            </div>
        )
    }

    function formatDate(date) {
        const [year, month, day] = date.split("-");
        return `${day}/${month}/${year}`;
    }

    function GetContent() {

        return (
            <div className={styles.mainContainer}>
                <div ref={pdfContentRef}>
                    <DocumentDefaultTemplate
                        date={formatDate(data.date)}
                        companyData={CompanyConfig}
                        patientData={patientData}
                        professionalData={professionalData}
                        title={data.title}
                        description={data.description}
                    />
                </div>
            </div>
        );
    }

    return <PopupBase Title={"Visualizar documento"} Content={GetContent()} Footer={GetFooter()} OnClose={OnCloseClickHandler} />
}

export default ViewDocumentPopup;